// src/pages/shops/AssignProductsPage.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ShoppingBag, Plus, Trash2, Edit2, ArrowLeft, AlertCircle,
  RefreshCw, Search, X, Package, ChevronDown, Check, Loader2, Info, Tag,
} from 'lucide-react';
import { PageHeader, DataTable, FilterPanel } from '../../components/common';
import {
  getShop,
  getShopAssignedProducts,
  assignProductsToShop,
  updateShopProduct,
  removeShopProduct,
} from '../../services/api/shops';
import { getProducts, getProductVariations } from '../../services/api/productSearch';
import { getProduct } from '../../services/api/products';
import { useLocalization } from '../../contexts/LocalizationContext';
import BundleBadge from '../../components/ui/BundleBadge/BundleBadge';
import { loadShopPriceMap, componentKey, round2 } from '../../components/product/BundleEditor/bundleUtils';
import { getImageUrl } from '../../utils/imageUtils';
import { useAuth } from '../../hooks/useAuth';
import OffersModal from '../products/components/OffersModal';
import styles from './AssignProductsPage.module.css';

// ─── Edit Modal ──────────────────────────────────────────────────────────────
const EditModal = ({ item, shopType, onClose, onSave }) => {
  const [price, setPrice] = useState(item.price ?? '');
  const [dropshippingPrice, setDropshippingPrice] = useState(item.dropshippingPrice ?? '');
  const [overrideEnabled, setOverrideEnabled] = useState(item.overrideDeliveryCharge != null);
  const [overrideDeliveryCharge, setOverrideDeliveryCharge] = useState(item.overrideDeliveryCharge ?? '');
  const [priority, setPriority] = useState(item.priority ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSave = async () => {
    if (price === '' || isNaN(parseFloat(price))) {
      setError('Price is required');
      return;
    }
    if (priority !== '' && (!Number.isInteger(Number(priority)) || Number(priority) < 1)) {
      setError('Priority must be a whole number of 1 or more');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await onSave({
        price: parseFloat(price),
        dropshippingPrice: shopType === 'dropshipper' && dropshippingPrice !== ''
          ? parseFloat(dropshippingPrice) : null,
        overrideDeliveryCharge: overrideEnabled && overrideDeliveryCharge !== ''
          ? parseFloat(overrideDeliveryCharge) : null,
        priority: priority === '' ? null : Number(priority),
      });
      onClose();
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modal} onClick={e => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h3>Edit Assignment</h3>
          <button className={styles.modalClose} onClick={onClose}><X size={18} /></button>
        </div>
        <div className={styles.modalBody}>
          <p className={styles.modalProductName}>{item.displayName}</p>

          {error && <div className={styles.modalError}><AlertCircle size={14} />{error}</div>}

          <div className={styles.formGroup}>
            <label>Price <span className={styles.required}>*</span></label>
            <input
              type="number" step="0.01" min="0"
              value={price}
              onChange={e => setPrice(e.target.value)}
              className={styles.input}
              placeholder="0.00"
            />
          </div>

          <div className={styles.formGroup}>
            <label>Priority</label>
            <input
              type="number" step="1" min="1"
              value={priority}
              onChange={e => setPriority(e.target.value)}
              className={styles.input}
              placeholder="e.g. 1 (1 = highest, leave empty for none)"
            />
          </div>

          {shopType === 'dropshipper' && (
            <div className={styles.formGroup}>
              <label>Dropshipping Price</label>
              <input
                type="number" step="0.01" min="0"
                value={dropshippingPrice}
                onChange={e => setDropshippingPrice(e.target.value)}
                className={styles.input}
                placeholder="0.00"
              />
            </div>
          )}

          <div className={styles.formGroup}>
            <div className={styles.checkboxRow}>
              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={overrideEnabled}
                  onChange={e => {
                    setOverrideEnabled(e.target.checked);
                    if (!e.target.checked) setOverrideDeliveryCharge('');
                  }}
                />
                <span>Override Delivery Charge</span>
              </label>
            </div>
            {overrideEnabled && (
              <input
                type="number" step="0.01" min="0"
                value={overrideDeliveryCharge}
                onChange={e => setOverrideDeliveryCharge(e.target.value)}
                className={`${styles.input} ${styles.indented}`}
                placeholder="0.00"
              />
            )}
          </div>
        </div>
        <div className={styles.modalFooter}>
          <button className={styles.cancelButton} onClick={onClose}>Cancel</button>
          <button className={styles.saveButton} onClick={handleSave} disabled={saving}>
            {saving ? <Loader2 size={14} className={styles.spinner} /> : null}
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Assign Modal ─────────────────────────────────────────────────────────────
const AssignModal = ({ shop, onClose, onAssigned }) => {
  const { t } = useLocalization();
  const [step, setStep] = useState('search'); // 'search' | 'variations' | 'configure' | 'bundle'
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [variations, setVariations] = useState([]);
  const [loadingVariations, setLoadingVariations] = useState(false);
  const [variationMode, setVariationMode] = useState('all'); // 'all' | 'select'
  const [selectedVariationIds, setSelectedVariationIds] = useState([]);

  // Configure step
  const [items, setItems] = useState([]); // [{ productId, variationId, displayName, price, dropshippingPrice, overrideEnabled, overrideDeliveryCharge }]
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Bundle step
  const [bundleRows, setBundleRows] = useState([]); // [{ bundleItemId, name, quantity, unitValue, lineDiscount }]
  const [bundleMissing, setBundleMissing] = useState([]);
  const [loadingBundle, setLoadingBundle] = useState(false);

  const searchTimer = useRef(null);

  const doSearch = useCallback(async (term) => {
    if (!term.trim()) { setSearchResults([]); return; }
    setSearching(true);
    try {
      const res = await getProducts({ search: term, limit: 20, page: 1 });
      setSearchResults(res.products || []);
    } catch {
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  }, []);

  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => doSearch(searchTerm), 400);
    return () => clearTimeout(searchTimer.current);
  }, [searchTerm, doSearch]);

  const handleSelectBundle = async (product) => {
    setStep('bundle');
    setLoadingBundle(true);
    setBundleRows([]);
    setBundleMissing([]);
    try {
      const [res, priceMap] = await Promise.all([
        getProduct(product.productId || product.id),
        loadShopPriceMap(getShopAssignedProducts, shop.id),
      ]);
      const bundle = res.product || res.data || res;
      const bundleItems = [...(bundle.bundleItems || [])].sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
      const rows = bundleItems.map(bi => {
        const key = componentKey(bi.componentProductId || bi.componentProduct?.id, bi.componentVariationId);
        const existing = (bi.shopPrices || []).find(sp => String(sp.shopId) === String(shop.id));
        const variationLabel = bi.componentVariation?.sku ? ` (${bi.componentVariation.sku})` : '';
        return {
          key,
          bundleItemId: bi.id,
          name: `${bi.componentProduct?.name || ''}${variationLabel}`,
          quantity: parseFloat(bi.quantity),
          unitValue: existing ? parseFloat(existing.unitValue) : (priceMap[key] ?? ''),
          lineDiscount: existing ? parseFloat(existing.lineDiscount) || 0 : 0,
        };
      });
      setBundleMissing(rows.filter(r => !(r.key in priceMap)).map(r => r.name));
      setBundleRows(rows);
    } catch (e) {
      setError(e.response?.data?.message || t('shops.bundleLoadFailed'));
    } finally {
      setLoadingBundle(false);
    }
  };

  const updateBundleRow = (index, field, value) => {
    setBundleRows(prev => prev.map((r, i) => (i === index ? { ...r, [field]: value } : r)));
  };

  const bundleRowTotal = (r) => round2(r.quantity * (parseFloat(r.unitValue) || 0) - (parseFloat(r.lineDiscount) || 0));
  const bundlePrice = round2(bundleRows.reduce((s, r) => s + bundleRowTotal(r), 0));

  const handleAssignBundle = async () => {
    setError('');
    const invalid = bundleRows.find(r => {
      const value = parseFloat(r.unitValue);
      const discount = parseFloat(r.lineDiscount) || 0;
      return r.unitValue === '' || isNaN(value) || value < 0 || discount < 0 || discount > r.quantity * value + 0.001;
    });
    if (invalid) {
      setError(`${t('products.bundleDiscountInvalid')}: "${invalid.name}"`);
      return;
    }
    setSaving(true);
    try {
      await assignProductsToShop(shop.id, [{
        productId: selectedProduct.productId || selectedProduct.id,
        bundleItems: bundleRows.map(r => ({
          bundleItemId: r.bundleItemId,
          unitValue: round2(r.unitValue),
          lineDiscount: round2(r.lineDiscount),
        })),
      }]);
      onAssigned();
      onClose();
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Failed to assign');
    } finally {
      setSaving(false);
    }
  };

  const handleSelectProduct = async (product) => {
    setSelectedProduct(product);
    if (product.type === 'bundle') {
      handleSelectBundle(product);
      return;
    }
    if (product.productType === 'variable' || product.type === 'variable') {
      setStep('variations');
      setLoadingVariations(true);
      try {
        const res = await getProductVariations(product.productId || product.id);
        setVariations(res.variations || []);
      } catch {
        setVariations([]);
      } finally {
        setLoadingVariations(false);
      }
    } else {
      // simple product — go straight to configure
      setItems([{
        productId: product.productId || product.id,
        variationId: null,
        displayName: product.displayName || product.name,
        price: '',
        dropshippingPrice: '',
        overrideEnabled: false,
        overrideDeliveryCharge: '',
      }]);
      setStep('configure');
    }
  };

  const handleVariationConfirm = () => {
    const toAssign = variationMode === 'all'
      ? variations
      : variations.filter(v => selectedVariationIds.includes(v.id));

    setItems(toAssign.map(v => ({
      productId: selectedProduct.productId || selectedProduct.id,
      variationId: v.id,
      displayName: v.displayName || `${selectedProduct.displayName || selectedProduct.name} (${v.attributeDisplay || ''})`,
      price: '',
      dropshippingPrice: '',
      overrideEnabled: false,
      overrideDeliveryCharge: '',
    })));
    setStep('configure');
  };

  const updateItem = (index, field, value) => {
    setItems(prev => prev.map((it, i) => i === index ? { ...it, [field]: value } : it));
  };

  const handleAssign = async () => {
    setError('');
    for (let i = 0; i < items.length; i++) {
      if (items[i].price === '' || isNaN(parseFloat(items[i].price))) {
        setError(`Price is required for "${items[i].displayName}"`);
        return;
      }
    }
    setSaving(true);
    try {
      const payload = items.map(it => ({
        productId: it.productId,
        variationId: it.variationId,
        price: parseFloat(it.price),
        dropshippingPrice: shop.type === 'dropshipper' && it.dropshippingPrice !== ''
          ? parseFloat(it.dropshippingPrice) : null,
        overrideDeliveryCharge: it.overrideEnabled && it.overrideDeliveryCharge !== ''
          ? parseFloat(it.overrideDeliveryCharge) : null,
      }));
      await assignProductsToShop(shop.id, payload);
      onAssigned();
      onClose();
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Failed to assign');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={`${styles.modal} ${styles.modalLarge}`} onClick={e => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h3>
            {step === 'search' && 'Assign Product to Shop'}
            {step === 'variations' && 'Select Variations'}
            {step === 'configure' && 'Configure Pricing'}
            {step === 'bundle' && t('shops.bundleConfigure')}
          </h3>
          <button className={styles.modalClose} onClick={onClose}><X size={18} /></button>
        </div>

        <div className={styles.modalBody}>
          {error && <div className={styles.modalError}><AlertCircle size={14} />{error}</div>}

          {/* ── Step 1: Search ── */}
          {step === 'search' && (
            <div className={styles.searchStep}>
              <div className={styles.searchInputWrapper}>
                <Search size={16} className={styles.searchIcon} />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  placeholder="Search by name, SKU, barcode..."
                  className={styles.searchInput}
                  autoFocus
                />
                {searchTerm && (
                  <button className={styles.clearSearch} onClick={() => { setSearchTerm(''); setSearchResults([]); }}>
                    <X size={14} />
                  </button>
                )}
              </div>

              <div className={styles.searchResults}>
                {searching && (
                  <div className={styles.searchLoading}><Loader2 size={20} className={styles.spinner} />Searching...</div>
                )}
                {!searching && searchResults.length === 0 && searchTerm && (
                  <div className={styles.noResults}>No products found for "{searchTerm}"</div>
                )}
                {!searching && !searchTerm && (
                  <div className={styles.searchHint}>Type to search products</div>
                )}
                {searchResults.map((product, i) => (
                  <button key={i} className={styles.searchResultItem} onClick={() => handleSelectProduct(product)}>
                    <div className={styles.searchResultInfo}>
                      <span className={styles.searchResultName}>{product.displayName || product.name}</span>
                      <span className={styles.searchResultMeta}>
                        {product.sku && <span>SKU: {product.sku}</span>}
                        {product.barcode && <span>Barcode: {product.barcode}</span>}
                        {product.type === 'bundle' ? (
                          <BundleBadge />
                        ) : (
                          <span className={`${styles.typeBadge} ${styles[product.type || product.productType]}`}>
                            {product.type || product.productType}
                          </span>
                        )}
                      </span>
                    </div>
                    <ChevronDown size={16} style={{ transform: 'rotate(-90deg)', flexShrink: 0, color: 'var(--color-text-tertiary)' }} />
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* ── Step 2: Variations ── */}
          {step === 'variations' && (
            <div className={styles.variationsStep}>
              <div className={styles.selectedProductHeader}>
                <button className={styles.backButton} onClick={() => { setStep('search'); setSelectedProduct(null); setVariations([]); }}>
                  <ArrowLeft size={14} /> Back
                </button>
                <span className={styles.selectedProductName}>{selectedProduct?.displayName || selectedProduct?.name}</span>
              </div>

              <div className={styles.variationModeToggle}>
                <button
                  className={`${styles.modeButton} ${variationMode === 'all' ? styles.modeActive : ''}`}
                  onClick={() => setVariationMode('all')}
                >
                  Assign All Variations
                </button>
                <button
                  className={`${styles.modeButton} ${variationMode === 'select' ? styles.modeActive : ''}`}
                  onClick={() => setVariationMode('select')}
                >
                  Select Variations
                </button>
              </div>

              {loadingVariations ? (
                <div className={styles.searchLoading}><Loader2 size={20} className={styles.spinner} />Loading variations...</div>
              ) : variations.length === 0 ? (
                <div className={styles.noResults}>No variations found</div>
              ) : (
                <div className={styles.variationsList}>
                  {variationMode === 'all' && (
                    <p className={styles.allVariationsNote}>All {variations.length} variations will be assigned</p>
                  )}
                  {variations.map(v => (
                    <label key={v.id} className={`${styles.variationItem} ${variationMode === 'all' ? styles.variationItemDisabled : ''}`}>
                      {variationMode === 'select' && (
                        <input
                          type="checkbox"
                          checked={selectedVariationIds.includes(v.id)}
                          onChange={e => {
                            setSelectedVariationIds(prev =>
                              e.target.checked ? [...prev, v.id] : prev.filter(id => id !== v.id)
                            );
                          }}
                          className={styles.variationCheckbox}
                        />
                      )}
                      {variationMode === 'all' && <Check size={14} className={styles.checkIcon} />}
                      <div className={styles.variationInfo}>
                        <span className={styles.variationName}>{v.attributeDisplay || `Variation #${v.id}`}</span>
                        {v.sku && <span className={styles.variationMeta}>SKU: {v.sku}</span>}
                      </div>
                      <span className={styles.variationStock}>Stock: {v.stockQuantity}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── Bundle step ── */}
          {step === 'bundle' && (
            <div className={styles.configureStep}>
              <div className={styles.selectedProductHeader}>
                <button className={styles.backButton} onClick={() => { setStep('search'); setSelectedProduct(null); setBundleRows([]); setError(''); }}>
                  <ArrowLeft size={14} /> Back
                </button>
                <span className={styles.selectedProductName}>{selectedProduct?.displayName || selectedProduct?.name}</span>
                <BundleBadge />
              </div>

              {loadingBundle ? (
                <div className={styles.searchLoading}><Loader2 size={20} className={styles.spinner} /></div>
              ) : bundleMissing.length > 0 ? (
                <div className={styles.modalError}>
                  <AlertCircle size={14} />
                  {t('shops.bundleComponentsMissing', { products: bundleMissing.join(', ') })}
                </div>
              ) : (
                <div className={styles.configureItems}>
                  {bundleRows.map((row, index) => (
                    <div key={row.bundleItemId} className={styles.configureItem}>
                      <div className={styles.configureItemName}>{row.name} × {row.quantity}</div>
                      <div className={styles.configureFields}>
                        <div className={styles.formGroup}>
                          <label>{t('shops.unitValue')} <span className={styles.required}>*</span></label>
                          <input
                            type="number" step="0.01" min="0"
                            value={row.unitValue}
                            onChange={e => updateBundleRow(index, 'unitValue', e.target.value)}
                            className={styles.input}
                            placeholder="0.00"
                          />
                        </div>
                        <div className={styles.formGroup}>
                          <label>{t('shops.lineDiscount')}</label>
                          <input
                            type="number" step="0.01" min="0"
                            value={row.lineDiscount}
                            onChange={e => updateBundleRow(index, 'lineDiscount', e.target.value)}
                            className={styles.input}
                            placeholder="0.00"
                          />
                        </div>
                        <div className={styles.formGroup}>
                          <label>{t('products.lineTotal')}</label>
                          <span>{bundleRowTotal(row).toFixed(2)}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                  <div className={styles.configureItemName}>{t('shops.bundlePrice')}: {bundlePrice.toFixed(2)}</div>
                </div>
              )}
            </div>
          )}

          {/* ── Step 3: Configure ── */}
          {step === 'configure' && (
            <div className={styles.configureStep}>
              {selectedProduct && (
                <div className={styles.selectedProductHeader}>
                  <button className={styles.backButton} onClick={() => {
                    if (selectedProduct.type === 'variable' || selectedProduct.productType === 'variable') {
                      setStep('variations');
                    } else {
                      setStep('search');
                      setSelectedProduct(null);
                    }
                  }}>
                    <ArrowLeft size={14} /> Back
                  </button>
                  <span className={styles.selectedProductName}>{selectedProduct?.displayName || selectedProduct?.name}</span>
                </div>
              )}

              <div className={styles.configureItems}>
                {items.map((item, index) => (
                  <div key={index} className={styles.configureItem}>
                    <div className={styles.configureItemName}>{item.displayName}</div>

                    <div className={styles.configureFields}>
                      <div className={styles.formGroup}>
                        <label>Price <span className={styles.required}>*</span></label>
                        <input
                          type="number" step="0.01" min="0"
                          value={item.price}
                          onChange={e => updateItem(index, 'price', e.target.value)}
                          className={styles.input}
                          placeholder="0.00"
                        />
                      </div>

                      {shop.type === 'dropshipper' && (
                        <div className={styles.formGroup}>
                          <label>Dropshipping Price</label>
                          <input
                            type="number" step="0.01" min="0"
                            value={item.dropshippingPrice}
                            onChange={e => updateItem(index, 'dropshippingPrice', e.target.value)}
                            className={styles.input}
                            placeholder="0.00"
                          />
                        </div>
                      )}

                      <div className={styles.formGroup}>
                        <label className={styles.checkboxLabel}>
                          <input
                            type="checkbox"
                            checked={item.overrideEnabled}
                            onChange={e => {
                              updateItem(index, 'overrideEnabled', e.target.checked);
                              if (!e.target.checked) updateItem(index, 'overrideDeliveryCharge', '');
                            }}
                          />
                          <span>Override Delivery Charge</span>
                        </label>
                        {item.overrideEnabled && (
                          <input
                            type="number" step="0.01" min="0"
                            value={item.overrideDeliveryCharge}
                            onChange={e => updateItem(index, 'overrideDeliveryCharge', e.target.value)}
                            className={`${styles.input} ${styles.indented}`}
                            placeholder="0.00"
                          />
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className={styles.modalFooter}>
          <button className={styles.cancelButton} onClick={onClose}>Cancel</button>
          {step === 'variations' && (
            <button
              className={styles.saveButton}
              onClick={handleVariationConfirm}
              disabled={variationMode === 'select' && selectedVariationIds.length === 0}
            >
              Next: Configure Pricing
            </button>
          )}
          {step === 'bundle' && (
            <button
              className={styles.saveButton}
              onClick={handleAssignBundle}
              disabled={saving || loadingBundle || bundleMissing.length > 0 || bundleRows.length === 0}
            >
              {saving ? <Loader2 size={14} className={styles.spinner} /> : <Plus size={14} />}
              Assign to Shop
            </button>
          )}
          {step === 'configure' && (
            <button className={styles.saveButton} onClick={handleAssign} disabled={saving}>
              {saving ? <Loader2 size={14} className={styles.spinner} /> : <Plus size={14} />}
              Assign to Shop
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────
const AssignProductsPage = () => {
  const { shopId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [shop, setShop] = useState(null);
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [search, setSearch] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [sorting, setSorting] = useState({ orderBy: 'displayName', orderDirection: 'ASC' });

  const [editingItem, setEditingItem] = useState(null);
  const [offersItem, setOffersItem] = useState(null);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const searchTimer = useRef(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [shopRes, productsRes] = await Promise.all([
        shop ? Promise.resolve({ ...shop }) : getShop(shopId),
        getShopAssignedProducts(shopId, {
          search,
          page,
          limit: pageSize,
          orderBy: sorting.orderBy === 'displayName' ? 'name' : sorting.orderBy,
          orderDirection: sorting.orderDirection,
        }),
      ]);
      if (!shop) setShop(shopRes);
      else setShop(productsRes.shop || shop);
      setProducts(productsRes.products || []);
      setTotal(productsRes.total || 0);
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [shopId, search, page, pageSize, sorting]);

  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(fetchData, search ? 400 : 0);
    return () => clearTimeout(searchTimer.current);
  }, [fetchData]);

  // Initial shop load
  useEffect(() => {
    getShop(shopId).then(setShop).catch(() => {});
  }, [shopId]);

  const handleRemove = async (item) => {
    if (!window.confirm(`Remove "${item.displayName}" from this shop?`)) return;
    setDeletingId(item.productShopId);
    try {
      await removeShopProduct(shopId, item.productShopId);
      fetchData();
    } catch (e) {
      alert(e.response?.data?.message || 'Failed to remove');
    } finally {
      setDeletingId(null);
    }
  };

  const handleEditSave = async (data) => {
    await updateShopProduct(shopId, editingItem.productShopId, data);
    fetchData();
  };

  const columns = [
    {
      key: 'displayName', header: 'Product', sortable: true,
      render: (row) => (
        <div className={styles.productCell}>
          {row.image && <img src={getImageUrl(row.image)} alt="" className={styles.productThumb} onError={e => { e.target.style.display = 'none'; }} />}
          <div className={styles.productInfo}>
            <span className={styles.productName}>{row.displayName}</span>
            <span className={`${styles.typeBadge} ${styles[row.productType]}`}>{row.productType}</span>
          </div>
        </div>
      ),
    },
    {
      key: 'priority', header: 'Priority', width: '90px', sortable: true, align: 'center',
      render: (row) => (
        row.priority != null
          ? <span className={styles.priorityBadge}>{row.priority}</span>
          : <span className={styles.na}>—</span>
      ),
    },
    {
      key: 'sku', header: 'SKU', width: '120px',
      render: (row) => <span className={styles.mono}>{row.sku || '—'}</span>,
    },
    {
      key: 'barcode', header: 'Barcode', width: '130px',
      render: (row) => <span className={styles.mono}>{row.barcode || '—'}</span>,
    },
    {
      key: 'price', header: 'Price', width: '110px', sortable: true, align: 'right',
      render: (row) => (
        <span className={styles.price}>{row.price != null ? `LKR ${parseFloat(row.price).toFixed(2)}` : '—'}</span>
      ),
    },
    ...(shop?.type === 'dropshipper' ? [{
      key: 'dropshippingPrice', header: 'Dropship Price', width: '130px', align: 'right',
      render: (row) => (
        <span className={styles.price}>{row.dropshippingPrice != null ? `LKR ${parseFloat(row.dropshippingPrice).toFixed(2)}` : '—'}</span>
      ),
    }] : []),
    {
      key: 'overrideDeliveryCharge', header: 'Override Delivery', width: '140px', align: 'right',
      render: (row) => (
        row.overrideDeliveryCharge != null
          ? <span className={styles.overrideBadge}>LKR {parseFloat(row.overrideDeliveryCharge).toFixed(2)}</span>
          : <span className={styles.na}>Default</span>
      ),
    },
    {
      key: 'stockQuantity', header: 'Stock', width: '80px', align: 'right',
      render: (row) => (
        <span className={row.stockQuantity === 0 ? styles.stockZero : styles.stockOk}>{row.stockQuantity}</span>
      ),
    },
  ];

  const renderActions = (row) => (
    <div className={styles.rowActions}>
      <button
        className={`${styles.actionBtn} ${styles.editBtn}`}
        onClick={() => setEditingItem(row)}
        title="Edit pricing"
      >
        <Edit2 size={14} />
      </button>
      <button
        className={`${styles.actionBtn} ${styles.offersBtn} ${row.offers?.length > 0 ? styles.offersBtnActive : ''}`}
        onClick={() => setOffersItem(row)}
        title="Manage offers"
      >
        <Tag size={14} />
      </button>
      <button
        className={`${styles.actionBtn} ${styles.deleteBtn}`}
        onClick={() => handleRemove(row)}
        disabled={deletingId === row.productShopId}
        title="Remove from shop"
      >
        {deletingId === row.productShopId
          ? <Loader2 size={14} className={styles.spinner} />
          : <Trash2 size={14} />}
      </button>
    </div>
  );

  const isResellerShop = shop
    ? shop.resellerBusinessId === user?.businessId && shop.businessId !== user?.businessId
    : false;

  return (
    <div className={styles.page}>
      <PageHeader
        title={`Assign Products${shop ? ` — ${shop.name}` : ''}`}
        description="Manage products assigned to this shop and configure their pricing"
        icon={ShoppingBag}
        actions={
          <div className={styles.headerActions}>
            <Link to="/shops" className={styles.backLink}>
              <ArrowLeft size={16} /> Back to Shops
            </Link>
            {!isResellerShop && (
              <button className={styles.primaryButton} onClick={() => setShowAssignModal(true)}>
                <Plus size={16} /> Assign Product
              </button>
            )}
          </div>
        }
      />

      <FilterPanel
        filters={[]}
        values={{}}
        onChange={() => {}}
        show={showFilters}
        onToggle={() => setShowFilters(v => !v)}
        searchConfig={{
          show: true,
          placeholder: 'Search by name, SKU, barcode...',
          value: search,
          onChange: (val) => { setSearch(val); setPage(1); },
        }}
        pageSizeConfig={{
          show: true,
          value: pageSize,
          options: [10, 20, 50],
          onChange: (val) => { setPageSize(val); setPage(1); },
        }}
      />

      {isResellerShop && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          padding: '12px 16px',
          background: 'var(--color-info-bg, #eff6ff)',
          border: '1px solid var(--color-info-border, #bfdbfe)',
          borderRadius: '8px',
          color: 'var(--color-info-text, #1d4ed8)',
          fontSize: '14px',
          marginBottom: '12px',
        }}>
          <Info size={16} style={{ flexShrink: 0 }} />
          <span>Product assignment for this shop is managed through the <strong>Dropshipping Products</strong> page.</span>
        </div>
      )}

      {error && (
        <div className={styles.errorBanner}>
          <AlertCircle size={16} />
          <span>{error}</span>
          <button onClick={fetchData}><RefreshCw size={14} /></button>
        </div>
      )}

      <DataTable
        data={products}
        columns={columns}
        actions={{ render: renderActions, width: '100px', frozen: true }}
        loading={loading}
        emptyMessage="No products assigned to this shop"
        emptyDescription="Click 'Assign Product' to add products to this shop"
        emptyIcon={<Package size={48} style={{ color: '#9ca3af' }} />}
        sorting={sorting}
        onSortChange={setSorting}
        pagination={{ currentPage: page, pageSize, total, pageSizeOptions: [10, 20, 50] }}
        onPaginationChange={({ currentPage, pageSize: ps }) => {
          setPage(currentPage);
          if (ps !== pageSize) { setPageSize(ps); setPage(1); }
        }}
        stickyHeader
      />

      {editingItem && (
        <EditModal
          item={editingItem}
          shopType={shop?.type}
          onClose={() => setEditingItem(null)}
          onSave={handleEditSave}
        />
      )}

      {offersItem && (
        <OffersModal
          isOpen
          onClose={() => setOffersItem(null)}
          onSave={async (newOffers) => {
            await updateShopProduct(shopId, offersItem.productShopId, { offers: newOffers });
            setOffersItem(null);
            fetchData();
          }}
          offers={offersItem.offers || []}
          productName={offersItem.displayName}
        />
      )}

      {showAssignModal && shop && !isResellerShop && (
        <AssignModal
          shop={shop}
          onClose={() => setShowAssignModal(false)}
          onAssigned={() => { setPage(1); fetchData(); }}
        />
      )}
    </div>
  );
};

export default AssignProductsPage;